import { supportedFileTypes, supportedMediaTypes } from "@/app/workspace/components/chat/input/supportedFiles";
import { message, Modal } from "ant-design-vue";

const OneMegaByte = 1024 * 1024;

export const useFileHandler = (props: {
  multiple?: boolean;
  validatePerType?: boolean;

  validateTotalSize?: boolean;
  maxTotalSizeMB?: number;
  maxFileSizeMB?: number;
}) => {

  const isValidFileType = (file: File) => {
    const allTypes = [...supportedFileTypes, ...supportedMediaTypes];

    return allTypes.some(type => {
      if (type.includes("*")) {
        return file.type.startsWith(type.replace("*", ""));
      }
      return file.type === type;
    });
  };

  const disabledMaxFilesPerType = (type: 'file' | 'media', files: File[]) => {
    const maxFilesPerType = 5;

    if (props.validateTotalSize) {
      const maxTotalSize =
        (props.maxTotalSizeMB || 10) * OneMegaByte;

      const totalSize = files.reduce((acc, file) => {
        return acc + (file?.size || 0);
      }, 0);

      return totalSize >= maxTotalSize;
    }

    if (props.validatePerType) {
      const filesByType = {
        images: files.filter((f: File | string) => f instanceof File && f?.type.startsWith('image/')),
        videos: files.filter((f: File | string) => f instanceof File && f?.type.startsWith('video/')),
        documents: files.filter((f: File | string) => f instanceof File && !f?.type.startsWith('image/') && !f.type.startsWith('video/'))
      };

      if (type === 'file' && filesByType.documents.length >= maxFilesPerType) return true;
      if (type === 'media' && (filesByType.images.length >= maxFilesPerType && filesByType.videos.length >= maxFilesPerType)) return true;

      return false;
    } else {
      return files.length >= maxFilesPerType;
    }
  };

  const validateFileMultiple = (
    files: FileList,
    currentFiles: string[] | File[] = []
  ) => {
    if (files.length === 0) {
      message.error("No se seleccionó ningún archivo");
      return [];
    }

    const maxFileSize =
      (props.maxFileSizeMB || 5) * OneMegaByte;

    const maxTotalSize =
      (props.maxTotalSizeMB || 10) * OneMegaByte;

    const validFiles = Array.from(files).filter(
      (file) => file.size <= maxFileSize
    );

    if (validFiles.length < files.length) {
      if (!props.validateTotalSize)
        message.error(
          `No se agregaron archivos que superan ${props.maxFileSizeMB || 5}MB`
        );
      
      else {
        Modal.warning({
          title: `El archivo supera el límite de ${props.maxTotalSizeMB || 10}MB`,
          content: `Para compartir archivos más pesados, súbelos a un enlace externo (como Google Drive o Wetransfer) y colócalo en el cuerpo del correo.`,
          centered: true,
          okText: "Entendido",
        });

        return [];
      }
    }

    if (props.validateTotalSize) {
      const currentTotalSize = currentFiles.reduce((acc, file) => {
        if (file instanceof File) {
          return acc + file.size;
        }
        return acc;
      }, 0);

      const newFilesTotalSize = validFiles.reduce(
        (acc, file) => acc + file.size,
        0
      );
      
      if (
        currentTotalSize + newFilesTotalSize >
        maxTotalSize
      ) {          
        Modal.warning({
          title: `El total de archivos supera el límite ${props.maxTotalSizeMB || 10}MB`,
          content: `Para compartir archivos más pesados, súbelos a un enlace externo (como Google Drive o Wetransfer) y colócalo en el cuerpo del correo.`,
          centered: true,
          okText: "Entendido",
        });

        return [];
      }
    }

    return validFiles;
  };

  const filesValidatedByType = (filesValidated: File[], currentFiles: string[] | File[]) => {
    const maxFilesPerType = 5;

    if (props.validatePerType) {
      const filesByType = {
        images: currentFiles?.filter((f: File | string) => f instanceof File && f?.type.startsWith('image/')) || [],
        videos: currentFiles?.filter((f: File | string) => f instanceof File && f?.type.startsWith('video/')) || [],
        documents: currentFiles?.filter((f: File | string) => f instanceof File && !f?.type.startsWith('image/') && !f.type.startsWith('video/')) || []
      };

      const validatedByType = filesValidated.reduce((acc: File[], file: File) => {
        if (file.type.startsWith('image/') && filesByType.images.length < maxFilesPerType) {
          filesByType.images.push(file);
          acc.push(file);
        } else if (file.type.startsWith('video/') && filesByType.videos.length < maxFilesPerType) {
          filesByType.videos.push(file);
          acc.push(file);
        } else if (!file.type.startsWith('image/') && !file.type.startsWith('video/') && filesByType.documents.length < maxFilesPerType) {
          filesByType.documents.push(file);
          acc.push(file);
        }
        return acc;
      }, []);

      return validatedByType;
    } else {
      const files = [...currentFiles] || [];
      const validatedByType = filesValidated.reduce((acc: File[], file: File) => {
        if (files.length < maxFilesPerType) {
          files.push(file);
          acc.push(file);
        }
        return acc;
      }, []);

      return validatedByType;
    }
  };

  const validateFile = (file: File | undefined) => {
    if (!file) throw "No se seleccinó ningun archivo";

    if (file.size > 5 * OneMegaByte) throw "Supera el tamaño máximo de 5MB";
  };

  return {
    isValidFileType,
    validateFile,
    validateFileMultiple,
    disabledMaxFilesPerType,
    filesValidatedByType,
  };
};